import { FormEvent, useMemo } from 'react'

import { FormProvider, UseFormReturn } from 'react-hook-form'

import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'

import { T } from '../../i18n/index.js'

import { PollFormBody } from './PollFormBody.js'
import { PollFormContext } from './PollFormContext.js'
import { PollFormSettings } from './PollFormSettings.js'
import { PollFormSubmit } from './PollFormSubmit.js'
import { PollFormValues } from './types.js'

export interface PollFormProps {
  form: UseFormReturn<PollFormValues>

  onSubmit: (data: PollFormValues) => void

  onCancel: () => void

  isLoading?: boolean

  hasAlreadyVoted?: boolean

  mode?: 'create' | 'edit'
}

export function PollForm({
  form,
  onSubmit,
  onCancel,
  isLoading = false,
  hasAlreadyVoted = false,
  mode = 'create',
}: PollFormProps) {
  const handleSubmit = (e: FormEvent) => {
    // we need to do it to stop outer form from being submitted
    e.stopPropagation()
    form.handleSubmit(onSubmit)(e)
  }

  const contextValue = useMemo(
    () => ({ hasAlreadyVoted, mode }),
    [hasAlreadyVoted, mode],
  )

  return (
    <PollFormContext.Provider value={contextValue}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <Tabs.Group>
            <Tabs.List variant="plain" fullWidth>
              <Tabs.Tab name="poll">
                <T id="Generics.Details" defaultMessage="Details" />
              </Tabs.Tab>
              <Tabs.Tab name="settings">
                <T id="Generics.Settings" defaultMessage="Settings" />
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panels>
              <Tabs.Panel className="space-y-5" name="poll">
                <div className="overflow-y-auto space-y-5">
                  <PollFormBody form={form} />
                </div>
              </Tabs.Panel>
              <Tabs.Panel name="settings">
                <div className="overflow-y-auto space-y-5">
                  <PollFormSettings form={form} />
                </div>
              </Tabs.Panel>
            </Tabs.Panels>
          </Tabs.Group>
          <div className="sticky bottom-0 bg-surface">
            <Divider />

            <PollFormSubmit
              form={form}
              onCancel={onCancel}
              isLoading={isLoading}
              mode={mode}
            />
          </div>
        </form>
      </FormProvider>
    </PollFormContext.Provider>
  )
}
